<!-- frontend/src/views/admin/Profile.vue -->
<template>
  <div class="max-w-4xl">
    <!-- Identity card -->
    <div class="bg-white rounded-lg shadow overflow-hidden mb-6">
      <div class="px-6 py-8 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-200">
        <div class="flex items-center gap-5">
          <div class="h-20 w-20 shrink-0 rounded-full bg-purple-600 flex items-center justify-center text-white text-2xl font-bold">
            {{ userInitials }}
          </div>
          <div class="min-w-0">
            <h2 class="text-xl font-bold text-gray-900 truncate">
              {{ displayName }}
            </h2>
            <p class="text-sm text-gray-600 truncate">{{ profile.email || '—' }}</p>
            <span class="mt-2 inline-flex items-center gap-1.5 rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-700">
              <Shield class="h-3 w-3" />
              Administrator
            </span>
          </div>
        </div>
      </div>

      <!-- Account details -->
      <div class="p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <BadgeCheck class="h-4 w-4 text-gray-400" />
            Account Details
          </h3>
          <button
            v-if="!isEditing"
            class="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            @click="startEdit"
          >
            <Pencil class="h-3.5 w-3.5" />
            Edit
          </button>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Full Name</label>
            <input
              v-if="isEditing"
              v-model="editForm.name"
              maxlength="255"
              class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:ring-purple-500"
            />
            <p v-else class="mt-1 text-sm text-gray-900">{{ profile.name || '—' }}</p>
          </div>
          <div>
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Job Title</label>
            <select
              v-if="isEditing"
              v-model="editForm.jobTitle"
              class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:ring-purple-500 bg-white"
            >
              <option value="">None</option>
              <option v-for="t in JOB_TITLES" :key="t.key" :value="t.key">
                {{ t.label }} ({{ t.shortcut }})
              </option>
            </select>
            <p v-else class="mt-1 text-sm text-gray-900">{{ jobTitleLabel }}</p>
          </div>
          <div>
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
            <p class="mt-1 text-sm text-gray-900">{{ profile.email || '—' }}</p>
          </div>
          <div>
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">User ID</label>
            <p class="mt-1 text-sm text-gray-900 tabular-nums">{{ profile.id ?? '—' }}</p>
          </div>
        </div>

        <!-- Live preview of the decorated name while editing -->
        <div
          v-if="isEditing"
          class="mt-4 rounded-md bg-purple-50 border border-purple-200 px-4 py-3 text-sm text-purple-800"
        >
          Your name will be displayed as:
          <span class="font-semibold">{{ editPreviewName }}</span>
        </div>

        <div v-if="saveError" class="mt-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {{ saveError }}
        </div>
        <div v-if="saveSuccess" class="mt-4 rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
          Profile updated successfully.
        </div>

        <div v-if="isEditing" class="mt-4 flex justify-end gap-2">
          <button
            class="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            :disabled="saving"
            @click="cancelEdit"
          >
            Cancel
          </button>
          <button
            class="inline-flex items-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
            :disabled="saving || !editForm.name.trim()"
            @click="saveProfile"
          >
            <Save class="h-4 w-4" />
            {{ saving ? 'Saving…' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Change password -->
    <div class="bg-white rounded-lg shadow p-6 mb-6">
      <h3 class="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <Lock class="h-4 w-4 text-gray-400" />
        Change Password
      </h3>
      <p class="text-xs text-gray-500 mb-4">
        Minimum {{ MIN_PASSWORD_LENGTH }} characters. You will keep your current session after changing it.
      </p>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Current Password</label>
          <input
            v-model="passwordForm.current"
            :type="showPasswords ? 'text' : 'password'"
            autocomplete="current-password"
            class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
          />
        </div>
        <div>
          <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">New Password</label>
          <input
            v-model="passwordForm.new"
            :type="showPasswords ? 'text' : 'password'"
            autocomplete="new-password"
            class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
          />
        </div>
        <div>
          <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Confirm New Password</label>
          <input
            v-model="passwordForm.confirm"
            :type="showPasswords ? 'text' : 'password'"
            autocomplete="new-password"
            class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
          />
        </div>
      </div>

      <div v-if="passwordError" class="mt-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
        {{ passwordError }}
      </div>
      <div v-if="passwordSuccess" class="mt-4 rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
        Password changed successfully.
      </div>

      <div class="mt-4 flex items-center justify-between">
        <label class="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
          <input v-model="showPasswords" type="checkbox" class="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
          Show passwords
        </label>
        <button
          class="inline-flex items-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
          :disabled="changingPassword || !passwordForm.current || !passwordForm.new || !passwordForm.confirm"
          @click="changePassword"
        >
          <Lock class="h-4 w-4" />
          {{ changingPassword ? 'Changing…' : 'Change Password' }}
        </button>
      </div>
    </div>

    <!-- Current session -->
    <div class="bg-white rounded-lg shadow p-6 mb-6">
      <h3 class="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Clock class="h-4 w-4 text-gray-400" />
        Current Session
      </h3>
      <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        <div>
          <dt class="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</dt>
          <dd class="mt-1">
            <span class="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
              <span class="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Active
            </span>
          </dd>
        </div>
        <div>
          <dt class="text-xs font-medium text-gray-500 uppercase tracking-wide">Session Expires</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ sessionExpiry || 'Unavailable' }}</dd>
        </div>
      </dl>
      <p class="mt-4 text-xs text-gray-500">
        Manage all active sessions from the
        <router-link to="/admin/sessions" class="text-purple-600 hover:text-purple-700 font-medium">Session Management</router-link>
        page.
      </p>
    </div>

    <!-- Quick links -->
    <div class="bg-white rounded-lg shadow p-6">
      <h3 class="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Zap class="h-4 w-4 text-gray-400" />
        Quick Links
      </h3>
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <router-link
          v-for="link in quickLinks"
          :key="link.href"
          :to="link.href"
          class="flex items-center gap-3 rounded-lg border border-gray-200 p-4 hover:border-purple-300 hover:bg-purple-50/50 transition-colors"
        >
          <component :is="link.icon" class="h-5 w-5 text-purple-600 shrink-0" />
          <div class="min-w-0">
            <p class="text-sm font-medium text-gray-900">{{ link.name }}</p>
            <p class="text-xs text-gray-500 truncate">{{ link.description }}</p>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import { JOB_TITLES, formatNameWithTitle } from '@/utils/jobTitles'
import {
  Shield, Clock, Zap, BadgeCheck, Pencil, Save, Lock,
  Settings, Key,
} from 'lucide-vue-next'

const MIN_PASSWORD_LENGTH = 8

const authStore = useAuthStore()

const profile = ref({
  id: authStore.user?.id ?? null,
  name: authStore.user?.name || '',
  email: authStore.user?.email || '',
  jobTitle: authStore.user?.job_title || null,
})
const sessionExpiry = ref(null)

const isEditing = ref(false)
const saving = ref(false)
const saveError = ref(null)
const saveSuccess = ref(false)
const editForm = ref({ name: '', jobTitle: '' })

const passwordForm = ref({ current: '', new: '', confirm: '' })
const changingPassword = ref(false)
const passwordError = ref(null)
const passwordSuccess = ref(false)
const showPasswords = ref(false)

const displayName = computed(() =>
  formatNameWithTitle(profile.value.name, profile.value.jobTitle) || 'Administrator'
)
const editPreviewName = computed(() =>
  formatNameWithTitle(editForm.value.name.trim() || '…', editForm.value.jobTitle || null)
)
const jobTitleLabel = computed(() => {
  const t = JOB_TITLES.find(t => t.key === profile.value.jobTitle)
  return t ? `${t.label} (${t.shortcut})` : 'None'
})
const userInitials = computed(() => {
  const name = profile.value.name || ''
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || 'A'
})

const quickLinks = [
  { name: 'System Settings', description: 'Platform configuration', href: '/admin/settings', icon: Settings },
  { name: 'Sessions', description: 'Active user sessions', href: '/admin/sessions', icon: Key },
]

const fetchProfile = async () => {
  try {
    const response = await axios.get('/admin/profile')
    const p = response.data?.data?.profile
    if (p) {
      profile.value = { id: p.id, name: p.name, email: p.email, jobTitle: p.jobTitle }
    }
  } catch (err) {
    console.error('Failed to load admin profile:', err)
  }
  try {
    const response = await axios.get('/auth/me')
    const expiresAt = response.data?.data?.session?.expiresAt
    if (expiresAt) {
      sessionExpiry.value = new Date(expiresAt).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      })
    }
  } catch (err) {
    console.error('Failed to load session details:', err)
  }
}

const startEdit = () => {
  editForm.value = {
    name: profile.value.name || '',
    jobTitle: profile.value.jobTitle || '',
  }
  saveError.value = null
  saveSuccess.value = false
  isEditing.value = true
}

const cancelEdit = () => {
  isEditing.value = false
  saveError.value = null
}

const saveProfile = async () => {
  if (saving.value) return
  saving.value = true
  saveError.value = null
  try {
    const response = await axios.put('/admin/profile', {
      name: editForm.value.name.trim(),
      jobTitle: editForm.value.jobTitle || null,
    })
    if (response.data?.success) {
      profile.value.name = editForm.value.name.trim()
      profile.value.jobTitle = editForm.value.jobTitle || null
      // Keep the cached auth user in sync so the sidebar/topbar update immediately
      if (authStore.user) {
        authStore.user.name = profile.value.name
        authStore.user.job_title = profile.value.jobTitle
        authStore.persistAuth()
      }
      isEditing.value = false
      saveSuccess.value = true
      setTimeout(() => { saveSuccess.value = false }, 4000)
    } else {
      saveError.value = response.data?.error || 'Failed to update profile'
    }
  } catch (err) {
    saveError.value = err.response?.data?.error || 'Failed to update profile'
  } finally {
    saving.value = false
  }
}

const changePassword = async () => {
  if (changingPassword.value) return
  passwordError.value = null
  passwordSuccess.value = false

  if (passwordForm.value.new.length < MIN_PASSWORD_LENGTH) {
    passwordError.value = `New password must be at least ${MIN_PASSWORD_LENGTH} characters`
    return
  }
  if (passwordForm.value.new !== passwordForm.value.confirm) {
    passwordError.value = 'New password and confirmation do not match'
    return
  }

  changingPassword.value = true
  try {
    const response = await axios.post('/admin/profile/change-password', {
      currentPassword: passwordForm.value.current,
      newPassword: passwordForm.value.new,
    })
    if (response.data?.success) {
      passwordSuccess.value = true
      passwordForm.value = { current: '', new: '', confirm: '' }
      setTimeout(() => { passwordSuccess.value = false }, 5000)
    } else {
      passwordError.value = response.data?.error || 'Failed to change password'
    }
  } catch (err) {
    passwordError.value = err.response?.data?.error || 'Failed to change password'
  } finally {
    changingPassword.value = false
  }
}

onMounted(fetchProfile)
</script>
